import { isBadgeReleased } from 'features/catalog/badge-release';
import { Badge } from 'features/catalog/useBadges';
import { getFirstPartOfEmail } from 'shared/lib/email';

export type ContributorIds = {
  creditIds: string[];
  designIds: string[];
};

const EMPTY: ContributorIds = { creditIds: [], designIds: [] };

/** Extract local-parts from a credits/design field (supports one or more `user@…` tokens). */
const localsFromField = (field: string | undefined): string[] => {
  if (!field) return [];
  const locals: string[] = [];
  const re = /([^\s,@]+)@/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(field)) !== null) {
    locals.push(match[1]);
  }
  return locals;
};

/**
 * One pass over the badge catalog → localPart → credit/design badge ids.
 * Replaces repeated `[...badgesMap.values()].filter(includes(email))` scans.
 */
export const buildContributorIndex = (badgesMap: Map<string, Badge>): Map<string, ContributorIds> => {
  const index = new Map<string, ContributorIds>();

  const ensure = (local: string): ContributorIds => {
    let entry = index.get(local);
    if (!entry) {
      entry = { creditIds: [], designIds: [] };
      index.set(local, entry);
    }
    return entry;
  };

  for (const badge of badgesMap.values()) {
    if (!isBadgeReleased(badge.id)) continue;
    const creditLocals = localsFromField(badge.credits);
    for (const local of creditLocals) {
      ensure(local).creditIds.push(badge.id);
    }
    const designLocals = localsFromField(badge.design);
    // No separate designer → idea person also designed it (matches Modal ✏️ & 🎨).
    const designAttribution = designLocals.length > 0 ? designLocals : creditLocals;
    for (const local of designAttribution) {
      ensure(local).designIds.push(badge.id);
    }
  }

  return index;
};

export const getContributorIds = (
  index: Map<string, ContributorIds>,
  emailOrLocal: string | null | undefined
): ContributorIds => {
  if (!emailOrLocal) return EMPTY;
  const local = emailOrLocal.includes('@') ? getFirstPartOfEmail(emailOrLocal) : emailOrLocal;
  return index.get(local) ?? EMPTY;
};

/** Union of credit + design ids (matches prior OR-scan semantics for submission counts). */
export const contributorBadgeCount = (ids: ContributorIds): number => {
  if (ids.creditIds.length === 0) return ids.designIds.length;
  if (ids.designIds.length === 0) return ids.creditIds.length;
  return new Set([...ids.creditIds, ...ids.designIds]).size;
};
